import React, { useMemo } from 'react';
import classnames from 'classnames';
import NumArrow from '@/pages/intelligence/pages/TopCharts/components/DetailsPage/NumArrow';
import Default from '@/pages/home/assets/svg/dashboard/default.svg';
import IconTipsCom from '@sg/components/IconTipsCom';
import { getNumConfig } from '@sg/utils/common';
import { formatMainValue } from '@sg/pages/StudioManagement/LiveTitles/pages/LiveVisualizationAll/utils';
import { LiveMetricTrendChart, LIVE_METRIC_TREND_CHART_HEIGHT } from '@sg/pages/StudioManagement/LiveTitles/utils/liveMetricTrendChart';
import type { IGggFinanceProjectMetric } from '../../../../types';
import { buildFinanceChannelRankData, buildTrendData, formatMoM } from '../utils';
import './index.scss';

interface IRevenueAfterRefundCellProps {
  title: string;
  tipString?: string;
  gameTitle?: string;
  metric?: IGggFinanceProjectMetric;
  baseGameUnits?: IGggFinanceProjectMetric;
  unit?: string;
}

const formatChannelValue = (value?: number | string | null): string => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return formatMainValue(Number(value), 'db_bmk0');
};

const getBarWidth = (subValue: number | string): string => {
  const ratio = Number(subValue);
  if (Number.isNaN(ratio) || ratio <= 0) {
    return '0%';
  }
  return `${Math.min(ratio * 100, 100)}%`;
};

const RevenueAfterRefundCell: React.FC<IRevenueAfterRefundCellProps> = ({
  title,
  tipString,
  gameTitle,
  metric,
  baseGameUnits,
  unit = 'USD',
}) => {
  const momText = formatMoM(metric?.mom);
  const numArrowData = getNumConfig(momText, 0);
  const isNullMom =
    metric?.mom === null ||
    metric?.mom === undefined ||
    !numArrowData;

  const trendData = useMemo(
    () => buildTrendData(metric?.timeline, 'revenue_after_refund'),
    [metric?.timeline],
  );

  const rankData = useMemo(
    () => buildFinanceChannelRankData(metric?.channels, gameTitle),
    [gameTitle, metric?.channels],
  );

  const displayValue = useMemo(
    () => formatMainValue(metric?.value, 'db_bmk0', unit),
    [metric?.value, unit],
  );

  const baseUnitsValue = useMemo(() => {
    if (baseGameUnits?.value === null || baseGameUnits?.value === undefined) {
      return '';
    }
    return formatMainValue(baseGameUnits.value, 'db_bmk0');
  }, [baseGameUnits?.value]);

  return (
    <div className="dbs-ltf-revenue-cell">
      <div className="dbs-ltf-revenue-cell__header">
        <div className="dbs-ltf-revenue-cell__title">
          <span>{title}</span>
          {tipString ? <IconTipsCom tipString={tipString} mWidth={360} /> : null}
        </div>
        <div className="dbs-ltf-revenue-cell__value-row">
          <span className="dbs-ltf-revenue-cell__value">{displayValue}</span>
          <div className="dbs-ltf-revenue-cell__mom">
            <span className="num-gray">MoM</span>
            {isNullMom ? (
              <span className="data-null">-</span>
            ) : (
              <NumArrow dataContent={numArrowData} type="num" />
            )}
          </div>
          {baseUnitsValue ? (
            <div className="dbs-ltf-revenue-cell__subline">
              <span className="dbs-ltf-revenue-cell__subline-label">
                Base Game Units After Refund
              </span>
              <span className="dbs-ltf-revenue-cell__subline-value">
                {baseUnitsValue}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      <div className="dbs-ltf-revenue-cell__viz">
        {rankData.length > 0 ? (
          <div className="dbs-ltf-revenue-cell__channels">
            {rankData.map((channel, index) => {
              const isPlaceholder = Boolean(channel.isPlaceholder);
              const formattedValue = formatChannelValue(channel.value);
              const isNoValue = formattedValue === '-';

              return (
                <div
                  key={`${channel.nameEn}-${index}`}
                  className="dbs-ltf-revenue-cell__channel-row"
                >
                  <img
                    className={classnames('dbs-ltf-revenue-cell__channel-icon', {
                      'dbs-ltf-revenue-cell__channel-icon--placeholder': isPlaceholder,
                    })}
                    src={
                      isPlaceholder
                        ? channel.icon || Default
                        : isNoValue
                          ? Default
                          : channel.icon || Default
                    }
                    alt={channel.nameEn}
                  />
                  <span
                    className={classnames('dbs-ltf-revenue-cell__channel-name', {
                      'dbs-ltf-revenue-cell__channel-name--placeholder': isPlaceholder,
                    })}
                  >
                    {channel.nameEn || '-'}
                  </span>
                  <div className="dbs-ltf-revenue-cell__channel-bar">
                    <div
                      className="dbs-ltf-revenue-cell__channel-bar-fill"
                      style={{
                        width: isNoValue ? '0%' : getBarWidth(channel.subValue),
                        background: channel.color,
                      }}
                    />
                  </div>
                  <span className="dbs-ltf-revenue-cell__channel-value">
                    {formattedValue}
                  </span>
                </div>
              );
            })}
          </div>
        ) : null}
        <LiveMetricTrendChart
          title={title}
          trendData={trendData}
          formatter="db_bmk0"
          unit={unit}
          height={LIVE_METRIC_TREND_CHART_HEIGHT}
          className="dbs-ltf-revenue-cell__chart"
          tooltipClassName="dbs-ltf-chart-tooltip"
        />
      </div>
    </div>
  );
};

export default RevenueAfterRefundCell;
